import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { subscriptionApi } from '../../lib/subscriptionApi';
import { supabase } from '../../lib/supabase';
import RequestList from './RequestList';

const Dashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { subscription, refreshSubscription } = useSubscription();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showCancelMessage, setShowCancelMessage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Handle Stripe success/cancel URLs
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    console.log('Dashboard URL params:', { success, sessionId, canceled });

    if (success === 'true' && sessionId) {
      console.log('Processing successful payment with session ID:', sessionId);
      setShowSuccessMessage(true);
      // Handle successful payment
      handleSuccessfulPayment(sessionId);
    } else if (canceled === 'true') {
      console.log('Payment was canceled');
      setShowCancelMessage(true);
    }
  }, [searchParams]);

  const handleSuccessfulPayment = async (sessionId: string) => {
    try {
      console.log('Handling successful payment for session:', sessionId);
      
      // First, refresh the user session to ensure they're still logged in
      const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !currentUser) {
        console.error('User not authenticated after payment:', authError);
        // Redirect to login with a message
        navigate('/login?message=Please sign in to complete your subscription');
        return;
      }

      console.log('User authenticated:', currentUser.id);
      
      // Handle the successful payment
      const result = await subscriptionApi.handleSuccessfulPayment(sessionId);
      
      if (result.success) {
        console.log('Payment handled successfully');
        await refreshSubscription();
      } else {
        console.error('Failed to handle payment:', result.error);
      }
    } catch (error) {
      console.error('Error handling successful payment:', error);
    }
  };

  const handleManageSubscription = async () => {
    if (subscription?.stripe_customer_id) {
      try {
        await subscriptionApi.createPortalSession(subscription.stripe_customer_id);
      } catch (error) {
        console.error('Error creating portal session:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success/Cancel Messages */}
        {showSuccessMessage && (
          <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-800">
                  Payment Successful!
                </h3>
                <div className="mt-1 text-green-700">
                  <p>Your subscription has been activated. You can now create design requests.</p>
                </div>
              </div>
              <button
                onClick={() => setShowSuccessMessage(false)}
                className="text-green-400 hover:text-green-600 p-2 hover:bg-green-100 rounded-xl transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {showCancelMessage && (
          <div className="mb-8 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-2xl p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-800">
                  Payment Canceled
                </h3>
                <div className="mt-1 text-yellow-700">
                  <p>Your payment was canceled. You can try again or contact support if you need help.</p>
                </div>
              </div>
              <button
                onClick={() => setShowCancelMessage(false)}
                className="text-yellow-400 hover:text-yellow-600 p-2 hover:bg-yellow-100 rounded-xl transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-primary to-primary-dark rounded-2xl mb-6 shadow-xl">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Dashboard</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Welcome back, {user?.user_metadata?.full_name || user?.email}! Here's an overview of your account and requests.
          </p>
        </div>
        
        {/* Subscription Status */}
        {subscription && (
          <div className="mb-10 bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {subscription.plan?.name} Plan
                    </h3>
                    <p className="text-gray-600">Your current subscription status</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Status</h4>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                      subscription.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : subscription.status === 'past_due' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : subscription.status === 'unpaid' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscription.status === 'active' && (
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {subscription.status}
                    </span>
                  </div>
                  
                  {subscription.current_period_end && (
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Next Billing</h4>
                      <p className="text-gray-900 font-semibold">
                        {new Date(subscription.current_period_end).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Plan Type</h4>
                    <p className="text-gray-900 font-semibold">{subscription.plan?.name}</p>
                  </div>
                </div>
                
                {/* Payment Failure Warning */}
                {(subscription.status === 'past_due' || subscription.status === 'unpaid') && (
                  <div className="mt-6 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-red-800 mb-2">
                          Payment Failed
                        </h4>
                        <div className="text-red-700">
                          <p>
                            {subscription.status === 'past_due' 
                              ? 'Your payment was unsuccessful. Please update your payment method to continue your subscription.'
                              : 'Your subscription has been suspended due to payment failure. Please update your payment method to reactivate.'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex flex-col space-y-4 ml-8">
                {subscription.stripe_customer_id && (
                  <button
                    onClick={handleManageSubscription}
                    className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                      subscription.status === 'active' 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                        : 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
                    }`}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {subscription.status === 'active' ? 'Manage Subscription' : 'Update Payment Method'}
                  </button>
                )}
                
                {/* Show warning for past_due subscriptions */}
                {subscription.status === 'past_due' && (
                  <div className="text-center p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <p className="text-sm text-yellow-800 font-medium">
                      ⚠️ Your subscription will be suspended soon
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Requests Section */}
        <div className="space-y-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Your Requests</h2>
              <p className="text-gray-600">Track and manage your design requests</p>
            </div>
          </div>
          
          <RequestList />
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 